import { ChangeEvent, forwardRef, useCallback, useState } from 'react';
import MUISwitch from '@mui/material/Switch';
import withCx, { CxProps } from 'fe-core/util/withCx';
import { BaseInputProps } from 'fe-design-base/baseTypes';

import { EVENT_ACTIONS, TRACK_ACTION_TYPES } from 'util/tracking_constants';
import { useTrackUx } from 'util/uxEvents';

export type ToggleSwitchEvent = {
  target: { name: string; checked: boolean };
};

export interface ToggleSwitchProps
  extends Omit<BaseInputProps, 'onFocus' | 'onBlur' | 'onChange'> {
  checked?: boolean; // leave as optional for backwards compatibility
  uxElement?: string;
  onChange?: (e: ToggleSwitchEvent) => void; // leave as optional for backwards compatibility
  size?: 'small' | 'medium';
}

const ToggleSwitch = forwardRef<HTMLInputElement, ToggleSwitchProps & CxProps>(
  (
    {
      cx,
      cxEl,
      checked,
      disabled,
      onChange,
      name,
      uxElement,
      size = 'medium',
      ...props
    },
    ref
  ) => {
    const [isPressed, setIsPressed] = useState(false);
    const trackUx = useTrackUx({
      element: uxElement,
    } as any);

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        if (disabled) {
          e.preventDefault();
          return;
        }

        if (uxElement) {
          trackUx(EVENT_ACTIONS.SWITCH_CLICKED, TRACK_ACTION_TYPES.CLICK, {
            checked: e.target.checked,
          });
        }

        onChange?.({
          target: {
            name,
            checked: e.target.checked,
          },
        });
      },
      [disabled, onChange, trackUx, uxElement, name]
    );

    const handleMouseDown = useCallback(() => setIsPressed(true), []);
    const handleMouseUp = useCallback(() => setIsPressed(false), []);

    // handles edge case where the mouse is released outside of the component
    const handleMouseLeave = useCallback(() => setIsPressed(false), []);

    return (
      <MUISwitch
        aria-disabled={disabled}
        inputRef={ref}
        className={cx({ pressed: isPressed })}
        checked={checked}
        onChange={handleChange}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        disableRipple
        name={name}
        size={size}
        {...props}
      />
    );
  }
);

export default withCx<ToggleSwitchProps>('FDBSwitch')(ToggleSwitch);
